import { type AlertColor } from '@mui/material/Alert/Alert'
import { type AxiosError } from 'axios'
import React, {
  type Dispatch,
  type SetStateAction,
  createContext,
  useMemo,
  useState
} from 'react'
import { type QueryObserverResult } from 'react-query'
import { useBoolean } from 'react-use'

import { useGetAwsIamRoleMetadata } from 'queries/aws/use-get-aws-iam-role-metadata'

import type { AWSIAMRoleMetadata } from 'types/api/aws/metadata'
import type { EndpointResponse } from 'types/api/status-checks/status-check'

export const INITIAL_AUTHENTICATION_RESPONSE: AuthenticationResponse = {
  responseBody: '',
  message: ''
}

export interface AuthenticationResponse extends EndpointResponse {
  alertSeverity?: AlertColor
  message: string
}

interface ObservabilityToolAuthenticationContextValues {
  authenticationResponse: AuthenticationResponse
  awsIAMRoleMetadata: AWSIAMRoleMetadata | undefined
  awsIAMRoleMetadataError: AxiosError | null
  hasErrorAwsIAMRoleMetadata: boolean
  isCreatingAuthenticationOnly: boolean
  isDrawerFlow: boolean
  isEdit: boolean
  isFetchingAwsIAMRoleMetadata: boolean
  isOnboarding: boolean
  reFetchAwsIAMRoleMetadata: () => Promise<
    QueryObserverResult<AWSIAMRoleMetadata, AxiosError>
  >
  setAuthenticationResponse: Dispatch<SetStateAction<AuthenticationResponse>>
  setIsCreatingAuthenticationOnly: Dispatch<SetStateAction<boolean>>
  setIsEdit: Dispatch<SetStateAction<boolean>>
  setIsOnboarding: Dispatch<SetStateAction<boolean>>
}

export const ObservabilityToolAuthenticationContext = createContext<
  ObservabilityToolAuthenticationContextValues | undefined
>(undefined)

export const ObservabilityToolAuthenticationProvider: React.FC<
  Partial<ObservabilityToolAuthenticationContextValues>
> = ({ children, ...initialValues }) => {
  const [authenticationResponse, setAuthenticationResponse] = useState(
    initialValues?.authenticationResponse || INITIAL_AUTHENTICATION_RESPONSE
  )
  const [isCreatingAuthenticationOnly, setIsCreatingAuthenticationOnly] =
    useBoolean(initialValues?.isCreatingAuthenticationOnly || false)
  const [isDrawerFlow, setIsDrawerFlow] = useBoolean(
    initialValues?.isDrawerFlow || false
  )
  const [isEdit, setIsEdit] = useBoolean(initialValues?.isEdit || false)
  const [isOnboarding, setIsOnboarding] = useBoolean(
    initialValues?.isOnboarding || false
  )

  const {
    data: awsIAMRoleMetadata,
    isFetching: isFetchingAwsIAMRoleMetadata,
    isError: hasErrorAwsIAMRoleMetadata,
    error: awsIAMRoleMetadataError,
    refetch: reFetchAwsIAMRoleMetadata
  } = useGetAwsIamRoleMetadata()

  const value = useMemo(() => {
    return {
      authenticationResponse,
      awsIAMRoleMetadata,
      awsIAMRoleMetadataError,
      hasErrorAwsIAMRoleMetadata,
      isCreatingAuthenticationOnly,
      isDrawerFlow,
      isEdit,
      isFetchingAwsIAMRoleMetadata,
      isOnboarding,
      reFetchAwsIAMRoleMetadata,
      setAuthenticationResponse,
      setIsCreatingAuthenticationOnly,
      setIsEdit,
      setIsOnboarding,
      setIsDrawerFlow
    }
  }, [
    authenticationResponse,
    awsIAMRoleMetadata,
    awsIAMRoleMetadataError,
    hasErrorAwsIAMRoleMetadata,
    isCreatingAuthenticationOnly,
    isDrawerFlow,
    isEdit,
    isFetchingAwsIAMRoleMetadata,
    isOnboarding,
    reFetchAwsIAMRoleMetadata,
    setIsCreatingAuthenticationOnly,
    setIsDrawerFlow,
    setIsEdit,
    setIsOnboarding
  ])

  return (
    <ObservabilityToolAuthenticationContext.Provider value={value}>
      {children}
    </ObservabilityToolAuthenticationContext.Provider>
  )
}

export const useObservabilityToolAuthenticationContext =
  (): ObservabilityToolAuthenticationContextValues => {
    const context = React.useContext(ObservabilityToolAuthenticationContext)

    if (!context) {
      throw new Error(
        'useObservabilityToolAuthenticationContext must be used within an ObservabilityToolAuthenticationProvider'
      )
    }

    return context
  }
